import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Center} from 'src/design/atoms/Center';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Link} from 'react-router-dom';
import type {AssistantFeedbackTallyResponse} from 'src/api/ProactApiClient';
import {AssistantFeedbackRatingEnum} from 'src/api/ProactApiClient';
import {feedbackReasonLabel} from 'src/app/assistant/Feedback/messageFeedback';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {InlineStat} from 'src/design/molecules/InlineStat';
import {formatCount} from 'src/shared/utils/formatters';
import {feedbackViewSearch} from './controlCenterParams';

export interface AssistantAnswerQualitySectionProps {
  tally: AssistantFeedbackTallyResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Scope note under the stats, e.g. the window the tally covers. */
  scopeText: string;
}


/** Share of rated replies voted up, floored so one Down vote never shows as 100%. */
const satisfactionPercent = (up: number, down: number): number | null =>
  up + down > 0 ? Math.floor((up * 100) / (up + down)) : null;

export const AssistantAnswerQualitySection = ({
  tally,
  isLoading,
  isError,
  scopeText,
}: AssistantAnswerQualitySectionProps) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <Center py="md">
          <Loader size="sm" />
        </Center>
      );
    }
    if (isError || !tally) {
      return (
        <Alert color="red" title="Unable to load answer quality">
          Reply feedback could not be loaded. Please try again.
        </Alert>
      );
    }

    const up = tally.positiveCount;
    const down = tally.negativeCount;
    const satisfaction = satisfactionPercent(up, down);

    return (
      <>
        <SimpleGrid cols={{base: 2, sm: 3}} spacing="md">
          <InlineStat label="Positive" value={formatCount(up)} />
          <InlineStat label="Negative" value={formatCount(down)} />
          <InlineStat
            label="Satisfaction"
            value={satisfaction === null ? '—' : `${satisfaction}%`}
          />
        </SimpleGrid>
        <Text size="xs" c="dimmed">
          {up + down === 0
            ? `No replies were rated in ${scopeText}. Votes appear here once users rate Scout replies.`
            : scopeText}
        </Text>
        {down > 0 && (
          <>
            <Divider />
            <Text size="xs" fw={600} c="dimmed" tt="uppercase">
              Negative feedback by reason
            </Text>
            <Stack gap={6}>
              {tally.reasonCounts.map(({reason, count}) => (
                <Group key={reason} justify="space-between" wrap="nowrap">
                  <Anchor
                    component={Link}
                    size="sm"
                    to={feedbackViewSearch({
                      rating: AssistantFeedbackRatingEnum.Down,
                      reasons: [reason],
                    })}>
                    {feedbackReasonLabel(reason)}
                  </Anchor>
                  <Text size="sm" fw={600}>
                    {formatCount(count)}
                  </Text>
                </Group>
              ))}
            </Stack>
          </>
        )}
      </>
    );
  };

  return (
    <SectionCard title="Answer quality">
      <Stack gap="md" p="md">
        {renderBody()}
      </Stack>
    </SectionCard>
  );
};
